import Card from '@mui/material/Card';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import { t } from 'i18next';
import type { JSX } from 'react';
import { useFormContext } from 'react-hook-form';

import { AutocompleteFormField } from '~components/FormFields/AutocompleteFormField';
import {
  getHaulerRateTypeOptions,
  getRateTypeLabel,
} from '~pages/Settings/RatesManagement/rateUtils';

import {
  type OrderFormV3FieldValues,
  useOrderFormV3Context,
} from '../OrderFormV3Context';

/**
 * Renders the rate-details card. Render-only: the haulerRateType
 * autocomplete over the static Per Hour / Per Tonne option list. The whole
 * card is flag-gated exactly like legacy — via the context boolean, never
 * a local hook call.
 * TRE-15597: the card shows when EITHER showHaulerRateTypeCard or
 * tomlinsonOrderModal is on (legacy `shouldShowHaulerRateCard`), but the
 * field is only required under tomlinsonOrderModal — validation stays on
 * the Tomlinson bundle.
 */
export function RateDetailsPanel(): JSX.Element | null {
  const { flags } = useOrderFormV3Context();
  const {
    control,
    formState: { errors },
  } = useFormContext<OrderFormV3FieldValues>();

  const shouldShowHaulerRateCard =
    flags.showHaulerRateTypeCard || flags.tomlinsonOrderModal;
  if (!shouldShowHaulerRateCard) {
    return null;
  }

  return (
    <Card sx={{ p: 2, mb: 2 }}>
      <Typography variant="form-section-title" sx={{ mb: 1 }}>
        {`${t('order.form.rate_details')}`}
      </Typography>

      <Grid container spacing={2}>
        <Grid size={{ xs: 6 }} data-test-id="order-form-v3-hauler-rate-type">
          <AutocompleteFormField
            clearable
            control={control}
            disablePortal={false}
            errors={errors}
            getLabel={(item) => item?.label ?? getRateTypeLabel(item)}
            getValue={(item) => item?.value ?? item}
            isCheckEnabled={false}
            isRequired={flags.tomlinsonOrderModal}
            label={`${t('form_fields.hauler_rate_type')}`}
            list={getHaulerRateTypeOptions()}
            name="haulerRateType"
          />
        </Grid>
      </Grid>
    </Card>
  );
}
